import * as React from 'react';

import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/utils/shadcn';

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
    ({ className, ...props }, ref) => (
        <div
            role="table"
            ref={ref}
            className={cn('grid w-full caption-bottom grid-cols-5 text-sm', className)}
            {...props}
        />
    ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <div role="rowgroup" ref={ref} className={cn('contents', className)} {...props} />
    ),
);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <div
            role="rowgroup"
            ref={ref}
            className={cn('contents [&:last-child>div:last-child>div]:border-0', className)}
            {...props}
        />
    ),
);
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
    ({ className, ...props }, ref) => (
        <tfoot
            ref={ref}
            className={cn('border-t bg-gray-100/50 font-medium dark:bg-gray-800/50 last:[&>div]:border-b-0', className)}
            {...props}
        />
    ),
);
TableFooter.displayName = 'TableFooter';

interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    semantics?: boolean;
    selectable?: boolean;
}

const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
    ({ className, semantics, selectable, ...props }, ref) => (
        <div
            role="row"
            ref={ref}
            className={cn(
                'group contents transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 dark:data-[state=selected]:bg-gray-800',
                semantics ? '[&>div]:border-y' : '[&>div]:border-b',
                selectable
                    ? 'data-[state=selected]:[&>div]:bg-primary-50 hover:data-[state=selected]:[&>div]:bg-primary-100 cursor-pointer hover:[&>div]:bg-gray-100'
                    : '',
                className,
            )}
            {...props}
        />
    ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
    HTMLTableCellElement,
    React.ThHTMLAttributes<HTMLTableCellElement> & { semantics?: boolean }
>(({ className, semantics, ...props }, ref) => (
    <div
        role="rowheader"
        ref={ref}
        className={cn(
            'sticky top-14 z-10 flex min-h-13 items-center bg-gray-50 px-4 py-1 font-medium whitespace-nowrap text-gray-500 dark:bg-gray-900 dark:text-gray-400 [&:has([role=checkbox])]:pr-0',
            semantics ? 'top-0' : 'top-14',
            className,
        )}
        {...props}
    />
));
TableHead.displayName = 'TableHead';

const tableCellVariants = cva(
    'clamp-2 flex min-h-16 items-center p-4 transition-colors [&:has([role=checkbox])]:pr-0',
    {
        variants: {
            // Based on tailwind naming convention for the sake of consistency
            cellHorizontalPadding: {
                tight: 'px-2',
                snug: 'px-3',
                normal: 'px-4',
                relaxed: 'px-5',
                loose: 'px-6',
            },
            cellVerticalPadding: {
                tight: 'py-2',
                snug: 'py-3',
                normal: 'py-4',
                relaxed: 'py-5',
                loose: 'py-6',
            },
        },
        defaultVariants: {
            cellHorizontalPadding: 'normal',
            cellVerticalPadding: 'normal',
        },
    },
);

interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement>, VariantProps<typeof tableCellVariants> {}

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
    ({ className, cellHorizontalPadding, cellVerticalPadding, ...props }, ref) => (
        <div
            role="cell"
            ref={ref}
            className={cn(tableCellVariants({ cellHorizontalPadding, cellVerticalPadding, className }))}
            {...props}
        />
    ),
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
    ({ className, ...props }, ref) => (
        <caption ref={ref} className={cn('mt-4 text-sm text-gray-500 dark:text-gray-400', className)} {...props} />
    ),
);
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, tableCellVariants, TableCaption };
